import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const GalleryModuleEditor = ({ data, onChange, siteSlug }) => {
  const layout = data.layout || 'grid';
  const spacing = data.spacing || 'spaced';
  const columns = data.columns || 3;
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Gallery'} 
          onChange={(e) => onChange({...data, title: e.target.value})}
        />
      </div>
      
      {/* Layout Options */}
      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onChange({...data, layout: 'grid'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'grid' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Grid layout"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span className="text-xs">Grid</span>
          </button>
          <button
            onClick={() => onChange({...data, layout: 'masonry'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'masonry' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Masonry layout"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 11a1 1 0 011-1h4a1 1 0 011 1v8a1 1 0 01-1 1h-4a1 1 0 01-1-1v-8z" />
            </svg>
            <span className="text-xs">Masonry</span>
          </button>
          <button
            onClick={() => onChange({...data, layout: 'carousel'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'carousel' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Carousel/slider"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-xs">Carousel</span>
          </button>
          
          <div className="h-8 w-px bg-slate-600 mx-2" />
          
          <button
            onClick={() => onChange({...data, spacing: 'spaced'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              spacing === 'spaced' 
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Spaced with gaps"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4M14 6h4M4 12h4M14 12h4M4 18h4M14 18h4" />
            </svg>
            <span className="text-xs">Spaced</span>
          </button>
          <button
            onClick={() => onChange({...data, spacing: 'flush'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              spacing === 'flush' 
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Flush (no gaps)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs">Flush</span>
          </button>
        </div>
        
        {/* Column selector for grid */}
        {layout === 'grid' && (
          <div className="flex items-center gap-2 mt-3">
            <Label className="text-sm text-slate-400">Columns:</Label>
            {[2, 3, 4].map(col => (
              <button
                key={col}
                onClick={() => onChange({...data, columns: col})}
                className={cn(
                  "w-8 h-8 rounded border flex items-center justify-center text-sm font-medium transition-all",
                  columns === col 
                    ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                    : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
                )}
              >
                {col}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <Label>Images</Label>
        <GalleryUploader
          images={data.images || []}
          onChange={(images) => onChange({...data, images})}
          folder={`sites/${siteSlug}/gallery`}
          maxImages={20}
        />
      </div>
    </div>
  );
};

// Video Gallery Module Editor

